/* eslint-disable no-plusplus */
/* eslint-disable react/button-has-type */
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export function Pagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1, 2, 3);

      if (currentPage > 4) pages.push('...');

      if (currentPage > 3 && currentPage < totalPages - 3) {
        pages.push(currentPage);
      } else if (currentPage === 4 || currentPage === totalPages - 3) {
        pages.push(currentPage);
      }

      if (currentPage < totalPages - 3) pages.push('...');

      pages.push(totalPages - 2, totalPages - 1, totalPages);
    }
    return pages;
  };

  const pages = getPageNumbers();

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className='flex items-center gap-6'>
      {/* Showing X to Y of Z */}
      <p className='text-sm leading-5 text-gray-500'>
        Showing{' '}
        <span className='text-gray-950'>
          {startItem} to {endItem} of {totalItems}
        </span>{' '}
        items
      </p>

      {/* Page numbers */}
      <nav
        aria-label='Pagination'
        className='isolate inline-flex items-center overflow-hidden rounded-lg border border-gray-200'
      >
        {/* Previous Button */}
        <button
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`inline-flex h-9 w-[35px] items-center justify-center bg-white rounded-l-md ${
            currentPage === 1
              ? 'cursor-not-allowed text-gray-300'
              : 'text-gray-400 hover:bg-gray-50'
          }`}
        >
          <span className='sr-only'>Previous</span>
          <ChevronLeftIcon aria-hidden='true' className='size-6' />
        </button>

        {/* Page Numbers */}
        {pages.map((page) =>
          typeof page === 'number' ? (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? 'page' : undefined}
              className={`inline-flex h-9 w-10 items-center justify-center text-sm font-medium ${
                page === currentPage
                  ? 'bg-blue-50 text-[#0066CB]'
                  : 'bg-white text-gray-950 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ) : (
            <span
              key={page}
              className='inline-flex h-9 w-10 items-center justify-center bg-white text-sm font-medium text-gray-950'
            >
              {page}
            </span>
          )
        )}

        {/* Next Button */}
        <button
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          className={`inline-flex h-9 w-[35px] items-center justify-center bg-white rounded-r-md ${
            currentPage === totalPages
              ? 'cursor-not-allowed text-gray-300'
              : 'text-gray-400 hover:bg-gray-50'
          }`}
        >
          <span className='sr-only'>Next</span>
          <ChevronRightIcon aria-hidden='true' className='size-6' />
        </button>
      </nav>
    </div>
  );
}
